import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { 
  Upload, FileImage, Receipt, CheckCircle2, Loader2, Sparkles,
  Building, ChevronDown, ChevronRight, CreditCard, Banknote,
  Link2, Check, X, Pencil, ArrowRight, Package, ExternalLink, Truck, Calculator,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Valid ingredient categories
const VALID_CATEGORIES = [
  'dairy', 'meat', 'seafood', 'produce', 'dry_goods', 'beverages', 
  'bakery', 'frozen', 'condiments', 'oils', 'cleaning', 'packaging',
  'confectionery', 'snacks', 'other'
];

// Map common alternative category names to valid values
const CATEGORY_ALIASES = {
  'sweets': 'confectionery', 'candy': 'confectionery', 'chocolate': 'confectionery',
  'lollipops': 'confectionery', 'fruit': 'produce', 'vegetables': 'produce',
  'veg': 'produce', 'fish': 'seafood', 'poultry': 'meat', 'chicken': 'meat',
  'beef': 'meat', 'pork': 'meat', 'lamb': 'meat', 'drinks': 'beverages',
  'sauces': 'condiments', 'spices': 'condiments', 'herbs': 'produce',
  'bread': 'bakery', 'pastry': 'bakery', 'cakes': 'bakery',
  'household': 'cleaning', 'supplies': 'packaging', 'grocery': 'dry_goods',
  'canned': 'dry_goods', 'tinned': 'dry_goods', 'ambient': 'dry_goods',
};

// Normalize a category to a valid enum value
const normalizeCategory = (category) => {
  if (!category) return 'other';
  const cat = category.toLowerCase().trim().replace(/\s+/g, '_');
  if (VALID_CATEGORIES.includes(cat)) return cat;
  if (CATEGORY_ALIASES[cat]) return CATEGORY_ALIASES[cat];
  // Partial match
  for (const [alias, valid] of Object.entries(CATEGORY_ALIASES)) {
    if (cat.includes(alias) || alias.includes(cat)) return valid;
  }
  return 'other';
};

const CONFIDENCE_STYLES = {
  high: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-red-100 text-red-800 border-red-200',
};

// ── Approval Row Component ──
const ApprovalRow = ({ suggestion, ingredients, onApprove, onReject, onEdit }) => {
  const conf = CONFIDENCE_STYLES[suggestion.confidence] || CONFIDENCE_STYLES.low;
  const isMemory = suggestion.source === 'previous_mapping';

  return (
    <TableRow className="group" data-testid={`approval-row-${suggestion.line_id}`}>
      {/* Raw Invoice Line */}
      <TableCell className="max-w-[200px]">
        <p className="font-medium text-sm truncate" title={suggestion.raw_description}>
          {suggestion.raw_description}
        </p>
        {suggestion.supplier_product_code && (
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-xs text-muted-foreground font-mono">{suggestion.supplier_product_code}</span>
            {suggestion.verify_url && (
              <a href={suggestion.verify_url} target="_blank" rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-700" title="Verify on supplier site">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </TableCell>

      {/* AI Suggestion */}
      <TableCell>
        <ArrowRight className="w-4 h-4 text-muted-foreground" />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-muted-foreground shrink-0" />
          <div>
            <p className="text-sm font-medium">
              {suggestion.ingredient_name || 'Unknown'}
              {suggestion.is_new_ingredient && (
                <Badge variant="outline" className="ml-2 text-[10px] py-0">NEW</Badge>
              )}
            </p>
            {suggestion.pack_format && (
              <p className="text-xs text-muted-foreground">{suggestion.product_name} ({suggestion.pack_format})</p>
            )}
            {suggestion.matched_modifier_name && (
              <p className="text-xs text-blue-600 mt-0.5">
                &rarr; {suggestion.matched_modifier_name} on <span className="font-medium">{suggestion.parent_item_name}</span>
              </p>
            )}
          </div>
        </div>
      </TableCell>

      {/* Quantity + Unit */}
      <TableCell className="text-center font-mono text-sm">
        {suggestion.normalized_quantity != null
          ? `${suggestion.normalized_quantity} ${suggestion.normalized_unit || ''}`
          : '—'}
      </TableCell>

      {/* Unit Cost */}
      <TableCell className="text-right font-mono text-sm">
        {suggestion.unit_cost != null ? fmt(suggestion.unit_cost) : '—'}
      </TableCell>

      {/* Confidence */}
      <TableCell>
        <Badge variant="outline" className={`text-[10px] ${conf}`}>
          {isMemory ? 'LEARNED' : suggestion.confidence?.toUpperCase()}
        </Badge>
      </TableCell>

      {/* Actions */}
      <TableCell>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:text-green-800 hover:bg-green-50"
            onClick={() => onApprove(suggestion)} title="Approve"
            data-testid={`approve-btn-${suggestion.line_id}`}>
            <Check className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            onClick={() => onEdit(suggestion)} title="Edit"
            data-testid={`edit-btn-${suggestion.line_id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onReject(suggestion)} title="Reject"
            data-testid={`reject-btn-${suggestion.line_id}`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

// ── Edit Dialog ──
const EditMappingDialog = ({ suggestion, ingredients, modifiers, open, onClose, onSave }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (suggestion) {
      setForm({
        ingredient_id: suggestion.ingredient_id || '',
        ingredient_name: suggestion.ingredient_name || '',
        is_new: suggestion.is_new_ingredient || false,
        quantity: suggestion.normalized_quantity || '',
        unit: suggestion.normalized_unit || 'kg',
        unit_cost: suggestion.unit_cost || 0,
        linked_modifier_id: suggestion.matched_modifier_id || '',
      });
    }
  }, [suggestion]);

  // Build modifier options grouped by parent item (must be before early return)
  const modifierOptions = useMemo(() => {
    if (!modifiers?.length) return [];
    const groups = {};
    modifiers.forEach(mod => {
      const parent = mod.parent_item_name || 'Other';
      if (!groups[parent]) groups[parent] = [];
      groups[parent].push(mod);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [modifiers]);

  if (!suggestion) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="edit-mapping-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Edit Mapping</DialogTitle>
          <p className="text-sm text-muted-foreground">{suggestion.raw_description}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Ingredient</Label>
            <Select value={form.ingredient_id || '__new__'}
              onValueChange={(val) => {
                if (val === '__new__') {
                  setForm({ ...form, ingredient_id: '', is_new: true });
                } else {
                  const ing = ingredients.find(i => i.id === val);
                  setForm({ ...form, ingredient_id: val, ingredient_name: ing?.name || '', is_new: false, unit: ing?.unit || form.unit });
                }
              }}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Select ingredient" /></SelectTrigger>
              <SelectContent>
                {ingredients.map(ing => (
                  <SelectItem key={ing.id} value={ing.id}>{ing.name} ({ing.unit})</SelectItem>
                ))}
                <SelectItem value="__new__">+ Create new ingredient</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.is_new && (
            <div className="space-y-1">
              <Label className="text-xs">New Ingredient Name</Label>
              <Input value={form.ingredient_name} className="h-9"
                onChange={(e) => setForm({ ...form, ingredient_name: e.target.value })} />
            </div>
          )}

          {/* Quantity Breakdown - helps calculate accurate cost per unit */}
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calculator className="w-3.5 h-3.5" />
              <span>Quantity Breakdown</span>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Packs/Cases</Label>
                <Input 
                  type="number" 
                  step="1" 
                  min="1"
                  value={form.pack_count || 1} 
                  className="h-9"
                  onChange={(e) => setForm({ ...form, pack_count: parseInt(e.target.value) || 1 })} 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Size/Pack</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  value={form.pack_size || ''} 
                  className="h-9"
                  placeholder="e.g. 2.5"
                  onChange={(e) => setForm({ ...form, pack_size: parseFloat(e.target.value) || 0 })} 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Unit</Label>
                <Select value={form.unit} onValueChange={(val) => setForm({ ...form, unit: val })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="ltr">ltr</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="each">each</SelectItem>
                    <SelectItem value="bottle">bottle</SelectItem>
                    <SelectItem value="loaf">loaf</SelectItem>
                    <SelectItem value="can">can</SelectItem>
                    <SelectItem value="pack">pack</SelectItem>
                    <SelectItem value="roll">roll</SelectItem>
                    <SelectItem value="case">case</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Calculated totals */}
            <div className="flex items-center justify-between pt-2 border-t text-sm">
              <div>
                <span className="text-muted-foreground">Total: </span>
                <span className="font-medium">
                  {((form.pack_count || 1) * (form.pack_size || form.quantity || 0)).toFixed(2)} {form.unit}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">@ </span>
                <span className="font-medium text-green-700">
                  {(() => {
                    const totalQty = (form.pack_count || 1) * (form.pack_size || form.quantity || 1);
                    const lineTotal = suggestion?.total_price || suggestion?.line_total || 0;
                    if (totalQty > 0 && lineTotal > 0) {
                      return `£${(lineTotal / totalQty).toFixed(3)}/${form.unit}`;
                    }
                    return `—`;
                  })()}
                </span>
              </div>
            </div>
          </div>

          {suggestion.verify_url && (
            <a href={suggestion.verify_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mt-2">
              <ExternalLink className="w-3.5 h-3.5" /> Verify on supplier site
            </a>
          )}

          {/* Link to Menu Item Modifier */}
          {modifierOptions.length > 0 && (
            <div className="space-y-1 border-t pt-3 mt-1">
              <Label className="text-xs">Link to Menu Modifier <span className="text-muted-foreground">(auto-creates recipe)</span></Label>
              <Select value={form.linked_modifier_id || '__none__'}
                onValueChange={(val) => setForm({ ...form, linked_modifier_id: val === '__none__' ? '' : val })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select modifier (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No modifier link</SelectItem>
                  {modifierOptions.map(([parentName, mods]) => (
                    <React.Fragment key={parentName}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/40">{parentName}</div>
                      {mods.map(mod => (
                        <SelectItem key={mod.id} value={mod.id}>{mod.item_name}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              {form.linked_modifier_id && (
                <p className="text-xs text-blue-600">
                  This ingredient will be linked to the modifier's recipe automatically.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => {
              const totalQty = (form.pack_count || 1) * (form.pack_size || form.quantity || 0);
              const lineTotal = suggestion?.total_price || suggestion?.line_total || 0;
              const calculatedUnitCost = totalQty > 0 && lineTotal > 0 ? lineTotal / totalQty : form.unit_cost || 0;
              onSave({ ...form, quantity: totalQty, unit_cost: calculatedUnitCost, line_id: suggestion.line_id });
            }}
            disabled={
              (!(form.pack_size || form.quantity)) && !(form.pack_count > 1) 
              || (!form.ingredient_id && !form.ingredient_name)
            }
            data-testid="save-edit-btn">
            <Check className="w-4 h-4 mr-1" /> Save & Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ── Main Page Component ──
const SmartInvoicePage = () => {
  const { currentLocation, api } = useAuth();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState('upload'); // upload, review, mapping, done
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [enriching, setEnriching] = useState(false);
  
  const [fileId, setFileId] = useState(null);
  const [fileName, setFileName] = useState('');
  const [filePreview, setFilePreview] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({});
  
  // Delivery spread info
  const [deliverySpread, setDeliverySpread] = useState(null);

  // Line item editing state
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [editingLineIndex, setEditingLineIndex] = useState(null);

  // Mapping state
  const [suggestions, setSuggestions] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  // Modifiers for linking
  const [modifiers, setModifiers] = useState([]);

  // Load ingredients and modifiers when entering mapping step
  useEffect(() => {
    if (step === 'mapping' && currentLocation) {
      Promise.all([
        api.get('/ingredients', { params: { location_id: currentLocation.id } }),
        api.get('/menu-item-masters', { params: { location_id: currentLocation.id, item_type: 'modifier' } }),
      ]).then(([ingRes, modRes]) => {
        setIngredients(ingRes.data || []);
        setModifiers(modRes.data?.items || []);
      }).catch(() => {});
    }
  }, [step, currentLocation]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image (PNG, JPG) or PDF');
      return;
    }
    
    setUploading(true);
    setFileName(file.name);
    
    if (file.type !== 'application/pdf') {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location_id', currentLocation.id);
    
    try {
      const res = await api.post('/expenses/smart-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setFileId(res.data.file_id);
        setExtraction(res.data.extraction);
        setLineItems(res.data.line_items || []);
        setDeliverySpread(res.data.delivery_spread || null);
        
        const paymentMethod = res.data.extraction?.payment_method || '';
        const isPaidByCard = /card|visa|mastercard|amex|contactless|apple pay|google pay/i.test(paymentMethod);
        
        setExpenseForm({
          vendor: res.data.extraction?.supplier_name || 'Unknown',
          date: res.data.extraction?.invoice_date || new Date().toISOString().split('T')[0],
          total_amount: res.data.extraction?.total || 0,
          category: 'cogs',
          invoice_number: res.data.extraction?.invoice_number || '',
          description: '',
          is_paid: isPaidByCard,
          payment_method: paymentMethod
        });
        
        setStep('review');
        
        // Show delivery spread notification
        if (res.data.delivery_spread) {
          const ds = res.data.delivery_spread;
          toast.success(`Delivery £${ds.delivery_total.toFixed(2)} spread across ${ds.items_spread_to} items (+£${ds.per_unit_surcharge.toFixed(4)}/unit)`);
        } else {
          toast.success(`Extracted ${res.data.line_items?.length || 0} line items`);
        }
        
        if (isPaidByCard) toast.info(`Payment detected: ${paymentMethod}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process invoice');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Line item editing handlers
  const handleEditLineItem = (item, index) => {
    setEditingLineItem({ ...item });
    setEditingLineIndex(index);
  };

  const handleSaveLineItem = () => {
    if (editingLineIndex === null) return;
    
    const updatedItems = [...lineItems];
    // Recalculate total_price based on quantity and unit_price
    const qty = parseFloat(editingLineItem.qty_ordered || editingLineItem.quantity || 1);
    const unitPrice = parseFloat(editingLineItem.unit_price || 0);
    updatedItems[editingLineIndex] = {
      ...editingLineItem,
      qty_ordered: qty,
      quantity: qty,
      unit_price: unitPrice,
      total_price: qty * unitPrice
    };
    setLineItems(updatedItems);
    
    // Also update the total amount in expense form
    const newTotal = updatedItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setExpenseForm(prev => ({ ...prev, total_amount: newTotal }));
    
    setEditingLineItem(null);
    setEditingLineIndex(null);
    toast.success('Line item updated');
  };

  const handleConfirmExpense = async () => {
    if (!fileId || !currentLocation) return;
    setConfirming(true);
    
    try {
      const res = await api.post('/expenses/smart-confirm', {
        location_id: currentLocation.id,
        file_id: fileId,
        vendor: expenseForm.vendor,
        date: expenseForm.date,
        total_amount: expenseForm.total_amount,
        category: expenseForm.category,
        description: expenseForm.description || `Invoice from ${expenseForm.vendor}`,
        invoice_number: expenseForm.invoice_number || '',
        is_paid: expenseForm.is_paid,
        payment_method: expenseForm.payment_method,
        line_items: lineItems
      });
      
      if (res.data.success) {
        toast.success('Expense created! Now map ingredients...');
        // Move to mapping step - trigger enrichment + suggestion
        setStep('mapping');
        loadSuggestions();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create expense');
    } finally {
      setConfirming(false);
    }
  };

  const loadSuggestions = async () => {
    setEnriching(true);
    try {
      // Step 1: Enrich
      await api.post('/invoice-lines/enrich', { file_id: fileId });
      // Step 2: Get suggestions
      const res = await api.post('/invoice-lines/suggest', { file_id: fileId });
      setSuggestions(res.data.suggestions || []);
      setApprovedCount(0);
      setRejectedCount(0);
    } catch (err) {
      toast.error('Failed to generate suggestions');
    } finally {
      setEnriching(false);
    }
  };

  const handleApprove = async (suggestion) => {
    try {
      let ingredientId = suggestion.ingredient_id;

      // Normalize unit/category to match backend enums
      const unitMap = { 'ltr': 'ltr', 'litre': 'L', 'litres': 'L', 'l': 'L', 'bottles': 'bottle', 'btl': 'bottle', 'loaves': 'loaf', 'cans': 'can', 'tins': 'can' };
      const normalizedUnit = unitMap[(suggestion.normalized_unit || '').toLowerCase()] || suggestion.normalized_unit || 'each';
      const normalizedCategory = normalizeCategory(suggestion.category);

      // If new ingredient, create it first
      if (suggestion.is_new_ingredient && !ingredientId) {
        const ingRes = await api.post('/ingredients', {
          location_id: currentLocation.id,
          name: suggestion.ingredient_name,
          unit: normalizedUnit,
          category: normalizedCategory,
          cost_per_unit: suggestion.unit_cost || 0,
          supplier_name: extraction?.supplier_name || expenseForm.vendor || null,
        });
        ingredientId = ingRes.data.id;
        setIngredients(prev => [...prev, ingRes.data]);
      }

      if (!ingredientId) {
        toast.error('No ingredient to map to');
        return;
      }

      await api.post('/ingredient-mappings', {
        invoice_line_id: suggestion.line_id,
        ingredient_id: ingredientId,
        normalized_quantity: suggestion.normalized_quantity || 0,
        normalized_unit: normalizedUnit,
        confidence: suggestion.confidence || 'medium',
        source: suggestion.source === 'previous_mapping' ? 'previous_mapping' : 'ai_suggestion',
        linked_modifier_id: suggestion.matched_modifier_id || null,
      });

      setSuggestions(prev => prev.filter(s => s.line_id !== suggestion.line_id));
      setApprovedCount(c => c + 1);
      toast.success(`Approved: ${suggestion.ingredient_name}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to approve');
      toast.error(msg);
    }
  };

  const handleReject = async (suggestion) => {
    try {
      await api.post(`/invoice-lines/${suggestion.line_id}/reject`);
      setSuggestions(prev => prev.filter(s => s.line_id !== suggestion.line_id));
      setRejectedCount(c => c + 1);
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const handleEditSave = async (form) => {
    try {
      let ingredientId = form.ingredient_id;
      const normalizedUnit = form.unit || 'each';

      // Create new ingredient if needed
      // Calculate total quantity from pack fields (already done in dialog Save click, but ensure fallback)
      const totalQty = form.quantity || 0;
      const unitCost = form.unit_cost || 0;

      if (form.is_new && !ingredientId) {
        const ingRes = await api.post('/ingredients', {
          location_id: currentLocation.id,
          name: form.ingredient_name,
          unit: normalizedUnit,
          category: 'other',
          cost_per_unit: unitCost,
          supplier_name: extraction?.supplier_name || expenseForm.vendor || null,
        });
        ingredientId = ingRes.data.id;
        setIngredients(prev => [...prev, ingRes.data]);
      }

      await api.post('/ingredient-mappings', {
        invoice_line_id: form.line_id,
        ingredient_id: ingredientId,
        normalized_quantity: totalQty,
        normalized_unit: normalizedUnit,
        confidence: 'high',
        source: 'manual',
        linked_modifier_id: form.linked_modifier_id || null,
      });

      setSuggestions(prev => prev.filter(s => s.line_id !== form.line_id));
      setApprovedCount(c => c + 1);
      setEditingSuggestion(null);
      toast.success('Mapping saved');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to save');
      toast.error(msg);
    }
  };

  const handleApproveAll = async () => {
    const approvable = suggestions.filter(s => s.ingredient_name && s.normalized_quantity);
    for (const s of approvable) {
      await handleApprove(s);
    }
  };

  const resetForm = () => {
    setStep('upload');
    setFileId(null);
    setFileName('');
    setFilePreview(null);
    setExtraction(null);
    setLineItems([]);
    setExpenseForm({});
    setSuggestions([]);
    setApprovedCount(0);
    setRejectedCount(0);
  };

  // ── STEP 1: Upload ──
  if (step === 'upload') {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="smart-invoice-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-primary" />
            Smart Invoice Scanner
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload an invoice or receipt to automatically extract costs and map ingredients
          </p>
        </div>
        
        <Card className="border-2 border-dashed hover:border-primary transition-colors">
          <CardContent className="p-12">
            <input ref={fileInputRef} type="file" accept="image/*,.pdf"
              onChange={handleFileUpload} className="hidden" data-testid="smart-invoice-file-input" />
            
            {uploading ? (
              <div className="text-center">
                <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Processing Invoice...</h3>
                <p className="text-muted-foreground">Extracting line items and totals</p>
              </div>
            ) : (
              <div className="text-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Upload className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Upload Invoice or Receipt</h3>
                <p className="text-muted-foreground mb-6">Drag and drop or click to browse</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Badge variant="outline">PDF</Badge>
                  <Badge variant="outline">PNG</Badge>
                  <Badge variant="outline">JPG</Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader><CardTitle className="text-base">How it works</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { icon: FileImage, color: 'blue', title: '1. Upload', desc: 'Upload invoice or receipt' },
                { icon: Sparkles, color: 'purple', title: '2. Extract', desc: 'AI reads all line items' },
                { icon: Receipt, color: 'green', title: '3. Track', desc: 'Create expense record' },
                { icon: Link2, color: 'amber', title: '4. Map', desc: 'Link items to ingredients' },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="text-center">
                  <div className={`w-12 h-12 rounded-full bg-${color}-100 flex items-center justify-center mx-auto mb-3`}>
                    <Icon className={`w-6 h-6 text-${color}-600`} />
                  </div>
                  <h4 className="font-medium mb-1">{title}</h4>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── STEP 2: Review ──
  if (step === 'review') {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-4" data-testid="smart-invoice-review">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Review Invoice</h1>
            <p className="text-sm text-muted-foreground">{fileName}</p>
          </div>
          <Button variant="outline" size="sm" onClick={resetForm}>Start Over</Button>
        </div>
        
        {/* Collapsible Invoice Details */}
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <Card className="bg-muted/30">
            <CollapsibleTrigger asChild>
              <CardHeader className="py-3 cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      {detailsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Building className="w-4 h-4" />
                      {extraction?.supplier_name || 'Invoice'}
                    </CardTitle>
                    <span className="text-sm font-mono font-bold">{fmt(extraction?.total)}</span>
                    <span className="text-sm text-muted-foreground">{extraction?.invoice_date}</span>
                    {extraction?.invoice_number && (
                      <span className="text-xs text-muted-foreground font-mono">#{extraction.invoice_number}</span>
                    )}
                  </div>
                  <Badge variant="outline">{lineItems.length} items</Badge>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {filePreview && (
                    <div>
                      <img src={filePreview} alt="Invoice preview"
                        className="rounded-md border max-h-[120px] object-contain bg-white" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-mono">{fmt(extraction?.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">VAT</span>
                      <span className="font-mono">{fmt(extraction?.vat_total)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium border-t pt-2">
                      <span>Total</span>
                      <span className="font-mono">{fmt(extraction?.total)}</span>
                    </div>
                  </div>
                  {extraction?.payment_method && (
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">Payment Method</span>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{extraction.payment_method}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Delivery Spread Info */}
        {deliverySpread && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                Delivery cost £{deliverySpread.delivery_total.toFixed(2)} spread across {deliverySpread.items_spread_to} items
              </span>
              <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                +£{deliverySpread.per_unit_surcharge.toFixed(4)}/unit
              </Badge>
            </div>
          </div>
        )}

        {/* Line Items */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Line Items</CardTitle>
            <p className="text-xs text-muted-foreground">Click a row to edit quantity or unit price</p>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Item</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.filter(item => !item.is_delivery).map((item, idx) => (
                    <TableRow 
                      key={idx} 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleEditLineItem(item, idx)}
                      data-testid={`line-item-row-${idx}`}
                    >
                      <TableCell>
                        <p className="font-medium text-sm truncate max-w-[300px]" title={item.product_name}>{item.product_name}</p>
                        {item.product_code && <p className="text-xs text-muted-foreground font-mono">{item.product_code}</p>}
                        {item.delivery_surcharge > 0 && (
                          <p className="text-xs text-emerald-600">+£{item.delivery_surcharge.toFixed(4)} delivery</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.qty_ordered || item.quantity || 1}
                        {item.pack_contents && <span className="text-muted-foreground"> x {item.pack_contents}</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmt(item.unit_price)}
                        {item.original_unit_price !== undefined && item.original_unit_price !== item.unit_price && (
                          <p className="text-xs text-muted-foreground line-through">{fmt(item.original_unit_price)}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium">{fmt(item.total_price)}</TableCell>
                      <TableCell>
                        <Pencil className="w-3 h-3 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Delivery items shown separately at end */}
                  {lineItems.filter(item => item.is_delivery).map((item, idx) => (
                    <TableRow key={`delivery-${idx}`} className="bg-emerald-50/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-emerald-600" />
                          <span className="font-medium text-sm text-emerald-800">{item.product_name}</span>
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-xs">Spread</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">-</TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">-</TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium text-emerald-700">{fmt(item.total_price)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ))}
                  {lineItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No line items extracted</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Expense Form */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-4 h-4" /> Create Expense
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Vendor</Label>
                <Input value={expenseForm.vendor || ''} className="h-9"
                  onChange={(e) => setExpenseForm({...expenseForm, vendor: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={expenseForm.date || ''} className="h-9"
                  onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Amount</Label>
                <Input type="number" step="0.01" value={expenseForm.total_amount || ''} className="h-9"
                  onChange={(e) => setExpenseForm({...expenseForm, total_amount: parseFloat(e.target.value)})} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={expenseForm.category || 'cogs'} onValueChange={(val) => setExpenseForm({...expenseForm, category: val})}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cogs">Cost of Goods</SelectItem>
                    <SelectItem value="operating">Operating</SelectItem>
                    <SelectItem value="labor">Labour</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Checkbox id="is_paid" checked={expenseForm.is_paid || false}
                  onCheckedChange={(checked) => setExpenseForm({...expenseForm, is_paid: checked})} />
                <div>
                  <Label htmlFor="is_paid" className="text-sm font-medium cursor-pointer">Mark as Paid</Label>
                  {expenseForm.payment_method && (
                    <p className="text-xs text-muted-foreground">Detected: {expenseForm.payment_method}</p>
                  )}
                </div>
              </div>
              <Badge className={expenseForm.is_paid ? 'bg-green-100 text-green-800' : 'text-amber-600 border-amber-300'}
                variant={expenseForm.is_paid ? 'default' : 'outline'}>
                {expenseForm.is_paid ? <><CreditCard className="w-3 h-3 mr-1" />Paid</> : <><Banknote className="w-3 h-3 mr-1" />Unpaid</>}
              </Badge>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button onClick={handleConfirmExpense}
                disabled={confirming || !expenseForm.vendor || !expenseForm.total_amount}
                className="gap-2" data-testid="confirm-smart-invoice-btn">
                {confirming ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : <><ArrowRight className="w-4 h-4" />Create & Map Ingredients</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Edit Line Item Dialog */}
        {editingLineItem && (
          <Dialog open={!!editingLineItem} onOpenChange={() => { setEditingLineItem(null); setEditingLineIndex(null); }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Line Item</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-sm font-medium">Product</Label>
                  <p className="text-sm text-muted-foreground mt-1">{editingLineItem.product_name}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="qty">Quantity</Label>
                    <Input
                      id="qty"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingLineItem.qty_ordered || editingLineItem.quantity || ''}
                      onChange={(e) => setEditingLineItem({...editingLineItem, qty_ordered: e.target.value, quantity: e.target.value})}
                      data-testid="edit-line-qty"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_price">Unit Price (£)</Label>
                    <Input
                      id="unit_price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingLineItem.unit_price || ''}
                      onChange={(e) => setEditingLineItem({...editingLineItem, unit_price: e.target.value})}
                      data-testid="edit-line-unit-price"
                    />
                  </div>
                </div>

                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Calculated Total:</span>
                    <span className="text-lg font-semibold">
                      £{((parseFloat(editingLineItem.qty_ordered || editingLineItem.quantity || 1)) * (parseFloat(editingLineItem.unit_price || 0))).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setEditingLineItem(null); setEditingLineIndex(null); }}>Cancel</Button>
                <Button onClick={handleSaveLineItem} data-testid="save-line-item-btn">Save Changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // ── STEP 3: Map Ingredients (Approval UI) ──
  if (step === 'mapping') {
    const total = suggestions.length + approvedCount + rejectedCount;

    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-4" data-testid="smart-invoice-mapping">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Map Ingredients</h1>
            <p className="text-sm text-muted-foreground">
              {extraction?.supplier_name} — {fmt(extraction?.total)} — {total} line items
            </p>
          </div>
          <div className="flex items-center gap-2">
            {suggestions.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleApproveAll} data-testid="approve-all-btn">
                <Check className="w-4 h-4 mr-1" /> Approve All ({suggestions.filter(s => s.ingredient_name && s.normalized_quantity).length})
              </Button>
            )}
            <Button size="sm" onClick={() => setStep('done')} data-testid="finish-mapping-btn">
              Done
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${total > 0 ? ((approvedCount + rejectedCount) / total * 100) : 0}%` }} />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-green-600 font-medium">{approvedCount} approved</span>
            <span className="text-red-500">{rejectedCount} rejected</span>
            <span className="text-muted-foreground">{suggestions.length} remaining</span>
          </div>
        </div>

        {enriching ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
              <p className="font-medium">Enriching line items...</p>
              <p className="text-sm text-muted-foreground">AI is identifying ingredients and quantities</p>
            </CardContent>
          </Card>
        ) : suggestions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-1">All items processed</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {approvedCount} approved, {rejectedCount} rejected
              </p>
              <Button onClick={() => setStep('done')} data-testid="go-to-done-btn">Continue</Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Invoice Line</TableHead>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Suggested Ingredient</TableHead>
                      <TableHead className="text-center">Quantity</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suggestions.map(s => (
                      <ApprovalRow key={s.line_id} suggestion={s} ingredients={ingredients}
                        onApprove={handleApprove} onReject={handleReject}
                        onEdit={(s) => setEditingSuggestion(s)} />
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        <EditMappingDialog suggestion={editingSuggestion} ingredients={ingredients} modifiers={modifiers}
          open={!!editingSuggestion} onClose={() => setEditingSuggestion(null)}
          onSave={handleEditSave} />
      </div>
    );
  }

  // ── STEP 4: Done ──
  if (step === 'done') {
    return (
      <div className="p-6 lg:p-8 max-w-2xl mx-auto space-y-6" data-testid="smart-invoice-success">
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Invoice Processed!</h2>
            <p className="text-muted-foreground mb-6">
              Expense created and ingredients mapped
            </p>
            
            <div className="bg-white rounded-lg p-4 mb-6 text-left max-w-sm mx-auto">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vendor</span>
                  <span className="font-medium">{expenseForm.vendor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono font-medium">{fmt(expenseForm.total_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ingredients Mapped</span>
                  <span className="font-medium text-green-600">{approvedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rejected</span>
                  <span className="font-medium text-muted-foreground">{rejectedCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment</span>
                  <Badge className={expenseForm.is_paid ? 'bg-green-100 text-green-800' : 'text-amber-600 border-amber-300'}
                    variant={expenseForm.is_paid ? 'default' : 'outline'}>
                    {expenseForm.is_paid ? 'Paid' : 'Unpaid'}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={resetForm}>Scan Another</Button>
              <Button onClick={() => window.location.href = '/expenses'}>View Expenses</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default SmartInvoicePage;
